import { useSidebarData, readStoredPlanName, readStoredGroupName, readStoredEmail } from "../../hooks/useSidebarData";
import { useLanguage } from "../../context/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import AuthService from "../../services/auth";
import PaymentService from "../../services/payment";
import UserService from "../../services/user";
import { disconnectGlobalSocket } from "../../services/socket";
import { setTokenStatus, updateUserProfile } from "../../store/slices/userSlice";
import { toastError, toastSuccess } from "../Toast";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import ProfileModal from "./ProfileModal";
import ContactUsPopup, { AlreadySentPopup } from "./ContactUsPopup";

const SidebarPanelContent = ({ onLogOut, onPlanClick, bottomPadding = "pb-[91px]", collapsed, expanded }) => {
  const { username, groupname, imagePreview } = useSidebarData();
  const { language, switchLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const reduxTokenData = useSelector((state) => state.user?.tokenData);
  const reduxUser = useSelector((state) => state.user?.user);

  const [storedPlanName, setStoredPlanName] = useState(() => (readStoredPlanName() || "").toLowerCase());

  const [tokenData, setTokenData] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
      return cached || null;
    } catch (_) { return null; }
  });
  const [activeSub, setActiveSub] = useState(() => {
    try {
      const cached = localStorage.getItem("linko_active_sub");
      return cached ? JSON.parse(cached) : null;
    } catch (_) {
      return null;
    }
  });
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [isContactUsSubmitting, setIsContactUsSubmitting] = useState(false);
  const [contactUsSuccess, setContactUsSuccess] = useState(false);
  const [showAlreadySentPopup, setShowAlreadySentPopup] = useState(false);
  const [rejectionNote, setRejectionNote] = useState(() => (localStorage.getItem("linko_rejection_note") || (localStorage.getItem("linko_contact_sent") === "true" ? "" : (reduxUser?.note || ""))));
  const [contactUsDisabled, setContactUsDisabled] = useState(() => {
    const note = localStorage.getItem("linko_rejection_note");
    if (note) return false;
    return (
      localStorage.getItem("linko_contact_sent") === "true" ||
      reduxUser?.is_contactus === true ||
      reduxUser?.is_contact_request === true ||
      reduxUser?.contact_request === true ||
      reduxUser?.is_request === true
    );
  });

  useEffect(() => {
    const syncStatus = () => {
      setStoredPlanName((readStoredPlanName() || "").toLowerCase());
      try {
        const cached = localStorage.getItem("linko_active_sub");
        setActiveSub(cached ? JSON.parse(cached) : null);
      } catch (_) { }
      const note = localStorage.getItem("linko_rejection_note") || (localStorage.getItem("linko_contact_sent") === "true" ? "" : (reduxUser?.note || ""));
      setRejectionNote(note);
      if (note) {
        setContactUsDisabled(false);
      } else {
        const isSent =
          localStorage.getItem("linko_contact_sent") === "true" ||
          reduxUser?.is_contactus === true ||
          reduxUser?.is_contact_request === true ||
          reduxUser?.contact_request === true ||
          reduxUser?.is_request === true;
        setContactUsDisabled(Boolean(isSent));
      }
    };

    syncStatus();
    window.addEventListener("profileUpdated", syncStatus);
    window.addEventListener("planUpdated", syncStatus);
    window.addEventListener("authChanged", syncStatus);
    window.addEventListener("contactStatusChanged", syncStatus);
    window.addEventListener("storage", syncStatus);
    return () => {
      window.removeEventListener("profileUpdated", syncStatus);
      window.removeEventListener("planUpdated", syncStatus);
      window.removeEventListener("authChanged", syncStatus);
      window.removeEventListener("contactStatusChanged", syncStatus);
      window.removeEventListener("storage", syncStatus);
    };
  }, [reduxUser]);

  const currentPlan = (
    reduxUser?.plan_name ||
    reduxUser?.trainingGroupId?.plan_name ||
    activeSub?.subscription_id?.plan_name ||
    activeSub?.plan_name ||
    storedPlanName ||
    ""
  ).toLowerCase();

  const isMissingVal = (v) => !v || v === "undefined" || v === "null";
  const rawGroupName = readStoredGroupName();
  const validGroupName = !isMissingVal(rawGroupName) ? rawGroupName : "";

  const hasContactPlan =
    currentPlan.includes("adminprovide") ||
    (!isMissingVal(currentPlan) && (currentPlan.includes("org") || currentPlan.includes("organiza") || currentPlan.includes("contact"))) ||
    Boolean(validGroupName) ||
    Boolean(reduxUser?.trainingGroupId);

  useEffect(() => {
    if (reduxTokenData) {
      setTokenData(reduxTokenData);
    }
  }, [reduxTokenData]);

  useEffect(() => {
    setImgError(false);
  }, [imagePreview]);

  useEffect(() => {
    if (!showCancelConfirm) return;
    const handleEsc = (e) => { if (e.key === "Escape" && !isCancelling) setShowCancelConfirm(false); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [showCancelConfirm, isCancelling]);

  useEffect(() => {
    if (!showLogoutConfirm) return;
    const handleEsc = (e) => { if (e.key === "Escape" && !isLoggingOut) setShowLogoutConfirm(false); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [showLogoutConfirm, isLoggingOut]);

  useEffect(() => {
    const syncSub = () => {
      try {
        const cached = localStorage.getItem("linko_active_sub");
        setActiveSub(cached ? JSON.parse(cached) : null);
      } catch (_) { }
    };

    window.addEventListener("profileUpdated", syncSub);
    window.addEventListener("planUpdated", syncSub);
    return () => {
      window.removeEventListener("profileUpdated", syncSub);
      window.removeEventListener("planUpdated", syncSub);
    };
  }, []);

  const handleCancelPlan = async () => {
    setIsCancelling(true);
    try {
      await PaymentService.cancelPlan(activeSub?.subscription_id?._id);
      toastSuccess(t.cancelSuccess || "Plan cancelled successfully.");
      setActiveSub(null);
      try {
        localStorage.removeItem("linko_active_sub");
        localStorage.removeItem("linko_cancel_at_period_end");
      } catch (_) { }
      dispatch(updateUserProfile({ cancel_at_period_end: true, is_subscription: false }));
      window.dispatchEvent(new Event("profileUpdated"));
      setShowCancelConfirm(false);
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to cancel subscription.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      if (onLogOut) {
        await onLogOut();
      } else {
        await AuthService.logout();
        try { indexedDB.deleteDatabase("linko_transcribe_db"); } catch (_e) { }
        disconnectGlobalSocket(dispatch);
        localStorage.clear();
        window.dispatchEvent(new Event("authChanged"));
        navigate("/login");
      }
    } catch (error) {
      toastError(error?.response?.data?.message || "Logout failed. Please try again.");
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  const handleLogOut = () => {
    setShowLogoutConfirm(true);
  };

  const handlePlanClick = onPlanClick ?? (() => {
    navigate("/plans", { state: { tab: "plans" } });
  });

  const handleContactUsSubmit = async (form) => {
    try {
      setIsContactUsSubmitting(true);
      const payload = {
        email: form.email,
        name: form.name,
        phone_no: form.phone_no,
        country_code: form.country_code,
        note: form.note,
      };
      await PaymentService.contactUsPlan(payload);
      setShowContactUs(false);
      setContactUsSuccess(true);
      setContactUsDisabled(true);
      setRejectionNote("");
      try {
        localStorage.setItem("linko_contact_sent", "true");
        localStorage.removeItem("linko_rejection_note");
      } catch (_) { }
      dispatch(updateUserProfile({ is_contact_request: true, is_contactus: true, note: null }));
      window.dispatchEvent(new CustomEvent("contactStatusChanged", { detail: { sent: true } }));
      window.dispatchEvent(new Event("planUpdated"));
      window.dispatchEvent(new Event("profileUpdated"));
    } catch (error) {
      toastError(
        error?.response?.data?.message ||
        "Failed to send request. Please try again."
      );
    } finally {
      setIsContactUsSubmitting(false);
    }
  };

  const totalTokens = tokenData?.total_tokens || 0;
  const usedTokens = tokenData?.used_tokens || 0;
  const usagePercentage = totalTokens > 0 ? (usedTokens / totalTokens) * 100 : 0;
  const showTokenWarning = tokenData !== null && usagePercentage >= 90;

  const handleLanguageSwitch = async (lang) => {
    switchLanguage(lang);
    try {
      const payload = lang === "en" ? "english" : "spanish";
      await UserService.updateLanguage(payload);
    } catch (err) {
      console.error("Failed to update language preference:", err);
    }
  };

  // Collapsed mode — just show profile icon strip
  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-3 pt-2">
        <div
          className="rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer"
          style={{
            width: "40px",
            height: "40px",
            background: "linear-gradient(to top right, #C7BFE5 0%, #904DB0 100%)",
          }}
          onClick={() => setShowProfileModal(true)}
          title={username || "Profile"}
        >
          {imagePreview && !imgError ? (
            <img
              src={imagePreview}
              alt="Profile"
              className="w-full h-full object-cover rounded-full"
              onError={() => setImgError(true)}
            />
          ) : (
            <span
              className="font-museo font-extrabold uppercase text-white leading-none select-none"
              style={{ fontSize: "16px" }}
            >
              {(username || "U").trim().charAt(0).toUpperCase() || "U"}
            </span>
          )}
        </div>
        <ProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
        />
      </div>
    );
  }

  // Expanded mode — full sidebar content
  return (
    <div className={`flex flex-col justify-between min-h-screen w-full pt-8 ${bottomPadding}`}>
      <div className="w-full flex flex-col items-center gap-4">
        {/* ENG / ESP toggle */}
        <div
          className="flex rounded-[22.5px] shadow-[inset_0_0_0_3px_#C7BFE5] h-[42px] min-w-[160px] overflow-hidden"
          style={{ background: "transparent" }}
        >
          <button
            onClick={() => handleLanguageSwitch("en")}
            className="flex-1 h-full flex items-center justify-center font-roboto font-extrabold text-[14px] uppercase leading-none transition-all rounded-r-full px-5 whitespace-nowrap"
            style={{
              color: language === "en" ? "#1A0E3E" : "#C7BFE5",
              background: language === "en" ? "#C7BFE5" : "transparent",
            }}
          >
            ENG
          </button>
          <button
            onClick={() => handleLanguageSwitch("es")}
            className="flex-1 h-full flex items-center justify-center font-roboto font-extrabold text-[14px] uppercase leading-none transition-all rounded-l-full px-5 whitespace-nowrap"
            style={{
              color: language === "es" ? "#1A0E3E" : "#C7BFE5",
              background: language === "es" ? "#C7BFE5" : "transparent",
            }}
          >
            ESP
          </button>
        </div>

        {/* Profile circle with Edit icon badge */}
        <div className="relative mt-[48px] cursor-pointer group" onClick={() => setShowProfileModal(true)} title="Edit Profile">
          <div
            className="rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
            style={{
              width: "133.21px",
              height: "133.21px",
              background: "linear-gradient(to top right, #C7BFE5 0%, #904DB0 100%)",
            }}
          >
            {imagePreview && !imgError ? (
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-full object-cover rounded-full"
                onError={() => setImgError(true)}
              />
            ) : (
              <span
                className="font-museo font-extrabold uppercase text-white leading-none select-none"
                style={{ fontSize: "52px" }}
              >
                {(username || "U").trim().charAt(0).toUpperCase() || "U"}
              </span>
            )}
          </div>
          <div className="absolute bottom-1 right-1 bg-[#904DB0] border-2 border-white p-2 rounded-full shadow-lg group-hover:scale-110 transition-all flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
        </div>

        {username && (
          <p
            onClick={() => setShowProfileModal(true)}
            className="w-full px-4 font-museo font-bold uppercase text-center leading-snug mt-[31.21px] cursor-pointer hover:underline text-base break-words whitespace-normal"
            style={{ color: "#904DB0" }}
            title="Edit Profile"
          >
            {username}
          </p>
        )}

        {groupname && (
          <p
            className="w-full px-4 font-museo font-bold uppercase text-center leading-snug text-[#C7BFE5] pb-5 break-words whitespace-normal"
            style={{ fontSize: "20px" }}
          >
            {groupname}
          </p>
        )}

        {/* {showTokenWarning && (
          <div
            onClick={handlePlanClick}
            className="w-full max-w-[260px] px-4 py-3 bg-[#FEF3C7] border border-[#F59E0B] rounded-[12px] cursor-pointer hover:bg-[#FDE68A] transition-colors mb-2 text-center shadow-sm"
          >
            <p className="text-[#92400E] font-bold text-[12px] leading-tight">
              ⚠️ {t.tokenWarning90ClickToUpgrade}
            </p>
          </div>
        )} */}
      </div>

      {showCancelConfirm && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4"
          onClick={() => { if (!isCancelling) setShowCancelConfirm(false); }}
        >
          <div
            className="relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-8 w-full max-w-[360px] flex flex-col items-center gap-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => { if (!isCancelling) setShowCancelConfirm(false); }}
              disabled={isCancelling}
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center text-white/60 hover:text-white transition-opacity disabled:opacity-30"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="18" fill="#ef4444" fillOpacity="0.25" />
                <path d="M12 12l12 12M24 12l-12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <h3 className="font-museo font-extrabold text-[20px] text-white text-center uppercase leading-tight">
              {t.cancelPlanTitle}
            </h3>
            <p className="font-museo text-[13px] text-[#D2C5E8] text-center leading-relaxed">
              {t.cancelPlanDesc}
            </p>
            <div className="flex gap-3 w-full mt-1">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={isCancelling}
                className="flex-1 h-[44px] rounded-[87px] border-2 border-[#C7BFE5] text-[#C7BFE5] font-museo font-bold text-[12px] uppercase hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                {t.keepPlan}
              </button>
              <button
                onClick={handleCancelPlan}
                disabled={isCancelling}
                className="flex-1 h-[44px] rounded-[87px] bg-red-500 hover:bg-red-600 text-white font-museo font-bold text-[12px] uppercase transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isCancelling ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : t.yesCancel}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full flex flex-col items-center gap-2 px-3">
        {!hasContactPlan && (
          <button
            onClick={() => {
              if (contactUsDisabled && !rejectionNote) {
                setShowAlreadySentPopup(true);
              } else {
                setShowContactUs(true);
              }
            }}
            className="font-museo font-bold text-[18px] uppercase text-[#1A0E3E] leading-none rounded-[6px] bg-[#C7BFE5] hover:bg-[#b5acd6] transition-all"
            style={{ width: "217.84px", height: "37.39px" }}
          >
            {contactUsDisabled && !rejectionNote
              ? (t.requestSent || "REQUEST SENT").toUpperCase()
              : (t.contactUs || "CONTACT US").toUpperCase()}
          </button>
        )}
        <button
          onClick={handlePlanClick}
          className="font-museo font-bold text-[18px] uppercase text-[#1A0E3E] leading-none rounded-[6px] bg-[#C7BFE5] hover:bg-[#b5acd6] transition-all"
          style={{ width: "217.84px", height: "37.39px" }}
        >
          {t.plan}
        </button>
        <button
          onClick={() =>
            window.open(
              `${process.env.PUBLIC_URL}/privacy-policy.html`,
              "_blank",
              "noopener,noreferrer"
            )
          }
          className="font-museo font-bold text-[18px] uppercase text-[#1A0E3E] leading-none rounded-[6px] bg-[#C7BFE5] hover:bg-[#b5acd6] transition-all"
          style={{ width: "217.84px", height: "37.39px" }}
        >
          {t.policies}
        </button>
        <button
          onClick={handleLogOut}
          className="font-museo font-bold text-[18px] uppercase text-[#1A0E3E] leading-none rounded-[6px] bg-[#C7BFE5] hover:bg-[#b5acd6] transition-all"
          style={{ width: "217.84px", height: "37.39px" }}
        >
          {t.logOut}
        </button>
      </div>

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      {showAlreadySentPopup && (
        <AlreadySentPopup
          t={t}
          onClose={() => setShowAlreadySentPopup(false)}
        />
      )}
      {showContactUs && (
        <ContactUsPopup
          t={t}
          onClose={() => setShowContactUs(false)}
          onSubmit={handleContactUsSubmit}
          isSubmitting={isContactUsSubmitting}
          isSuccess={false}
          prefilledEmail={readStoredEmail() || ""}
        />
      )}
      {contactUsSuccess && (
        <ContactUsPopup
          t={t}
          onClose={() => setContactUsSuccess(false)}
          onSubmit={() => { }}
          isSubmitting={false}
          isSuccess={true}
        />
      )}

      {showLogoutConfirm &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={() => {
              if (!isLoggingOut) setShowLogoutConfirm(false);
            }}
          >
            <div
              className="bg-white rounded-[27px] p-6 w-full max-w-md flex flex-col gap-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">
                {t?.logoutConfirmTitle || t?.logOut || "LOG OUT"}
              </h2>
              <p className="text-[13px] text-[#9190A4] text-center font-roboto">
                {t?.logoutConfirmMsg || "Are you sure you want to log out?"}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={isLoggingOut}
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#F3F1F9] transition-colors disabled:opacity-50"
                >
                  {t?.newSessionCancel || "CANCEL"}
                </button>
                <button
                  type="button"
                  disabled={isLoggingOut}
                  onClick={handleConfirmLogout}
                  className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#2e1f72] transition-colors disabled:opacity-50"
                >
                  {isLoggingOut ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    t?.newSessionConfirm || "CONFIRM"
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SidebarPanelContent;